/**
 * Espelho de ``src/permissions/policies.py`` no backend. Usado pelo UI
 * para esconder/desabilitar botoes -- backend continua sendo a fonte
 * da verdade (retorna 403 se errado).
 */

import { Action, type ActionValue } from "./actions";

import type { KbPermission, Membership, User } from "@/api/types";


const ADMIN_ONLY = new Set<ActionValue>([
  Action.USER_CREATED,
  Action.USER_INVITED,
  Action.USER_UPDATED,
  Action.USER_DEACTIVATED,
  Action.USER_REACTIVATED,
  Action.USER_ADMIN_GRANTED,
  Action.USER_ADMIN_REVOKED,
  Action.USER_READ_ALL,
  // Gestao de usuarios de servico + suas API keys: admin-only.
  Action.SERVICE_USER_CREATED,
  Action.SERVICE_USER_UPDATED,
  Action.SERVICE_USER_DEACTIVATED,
  Action.SERVICE_USER_REACTIVATED,
  Action.SERVICE_USER_KEY_CREATED,
  Action.SERVICE_USER_KEY_REVOKED,
  Action.TEAM_CREATED,
  Action.TEAM_UPDATED,
  Action.TEAM_DEACTIVATED,
  Action.TEAM_REACTIVATED,
  Action.COMPANY_CONTEXT_UPDATED,
  // M3 Fase 4: catalogo de LLMs e whitelist por time sao admin-only.
  Action.LLM_MODEL_CREATED,
  Action.LLM_MODEL_UPDATED,
  Action.LLM_MODEL_ACTIVATED,
  Action.LLM_MODEL_DEACTIVATED,
  Action.TEAM_ALLOWED_MODELS_UPDATED,
  // M4 Fase 6: tons de prompt sao admin-only.
  Action.PROMPT_TONE_UPDATED,
  // M6 Hardening: audit log viewer eh admin-only.
  Action.AUDIT_LOG_LIST,
]);

const TEAM_MEMBER_ACTIONS = new Set<ActionValue>([
  Action.TEAM_MEMBER_ADDED,
  Action.TEAM_MEMBER_ROLE_CHANGED,
  Action.TEAM_MEMBER_REMOVED,
]);

const AUTHENTICATED_ACTIONS = new Set<ActionValue>([
  Action.COMPANY_CONTEXT_READ,
  Action.USER_PII_FILTER_ENABLED,
  Action.USER_PII_FILTER_DISABLED,
  // M5: visibilidade real eh conferida no service (owner do thread / agent_owner_team).
  Action.THREAD_CREATED,
  Action.THREAD_UPDATED,
  Action.THREAD_CANCELLED,
  Action.THREAD_DELETED,
  Action.HITL_DECIDED,
  // M6 / Fase 8: install/uninstall/request sao autenticados.
  Action.AGENT_INSTALLED,
  Action.AGENT_UNINSTALLED,
  Action.AGENT_INSTALL_REQUESTED,
  Action.AGENT_INSTALL_APPROVED,
  Action.AGENT_INSTALL_REJECTED,
  // M6 / Fase 10: team_session CRUD eh autenticado; ownership no service.
  Action.TEAM_SESSION_CREATED,
  Action.TEAM_SESSION_UPDATED,
  Action.TEAM_SESSION_DELETED,
]);

const KB_CREATE_ACTIONS = new Set<ActionValue>([
  Action.KNOWLEDGE_BASE_CREATED,
]);

const KB_ADMIN_ACTIONS = new Set<ActionValue>([
  Action.KNOWLEDGE_BASE_UPDATED,
  Action.KNOWLEDGE_BASE_DELETED,
  Action.KNOWLEDGE_BASE_PERMISSION_GRANTED,
  Action.KNOWLEDGE_BASE_PERMISSION_REVOKED,
]);

const DOCUMENT_WRITE_ACTIONS = new Set<ActionValue>([
  Action.DOCUMENT_UPLOADED,
  Action.DOCUMENT_DELETED,
  Action.DOCUMENT_REINDEXED,
]);

// --- M3 Fase 5: MCP servers -------------------------------------------------
const MCP_CREATE_ACTIONS = new Set<ActionValue>([Action.MCP_SERVER_CREATED]);
const MCP_ADMIN_ACTIONS = new Set<ActionValue>([
  Action.MCP_SERVER_UPDATED,
  Action.MCP_SERVER_DELETED,
  Action.MCP_SERVER_CREDENTIALS_UPDATED,
  Action.MCP_SERVER_PERMISSION_GRANTED,
  Action.MCP_SERVER_PERMISSION_REVOKED,
]);

// --- Skills (paridade com MCP) ----------------------------------------------
const SKILL_CREATE_ACTIONS = new Set<ActionValue>([Action.SKILL_CREATED]);
const SKILL_ADMIN_ACTIONS = new Set<ActionValue>([
  Action.SKILL_UPDATED,
  Action.SKILL_DELETED,
  Action.SKILL_PERMISSION_GRANTED,
  Action.SKILL_PERMISSION_REVOKED,
]);

// --- M4 Fase 6: agentes -----------------------------------------------------
const AGENT_CREATE_ACTIONS = new Set<ActionValue>([Action.AGENT_CREATED]);
const AGENT_EDIT_ACTIONS = new Set<ActionValue>([
  Action.AGENT_UPDATED,
  Action.AGENT_DELETED,
  Action.AGENT_DUPLICATED,
  Action.AGENT_VISIBILITY_CHANGED,
  Action.AGENT_KNOWLEDGE_BASES_CHANGED,
  Action.AGENT_MCPS_CHANGED,
  Action.AGENT_SKILLS_CHANGED,
  // M6 / Fase 8: publish workflow validado via _can_edit_agent no service.
  Action.AGENT_PUBLISH_REQUESTED,
  Action.AGENT_UNPUBLISHED,
  Action.AGENT_PUBLISH_APPROVED,
  Action.AGENT_PUBLISH_REJECTED,
]);
const AGENT_AUTH_ACTIONS = new Set<ActionValue>([Action.AGENT_RATED]);

type SimpleResource = { team_id: string } | string | undefined;
type KbResource = {
  owner_team_id: string;
  kb_permission?: KbPermission | null;
};
type AgentResource = {
  owner_team_id: string;
  created_by_user_id?: string;
};

type Resource = SimpleResource | KbResource | AgentResource;

function resourceTeamId(resource: Resource): string | undefined {
  if (resource === undefined) return undefined;
  if (typeof resource === "string") return resource;
  if ("team_id" in resource) return resource.team_id;
  return undefined;
}

function isKbResource(r: Resource): r is KbResource {
  return (
    typeof r === "object" && r !== null && "owner_team_id" in r
  );
}

export function can(
  user: User | null,
  memberships: Membership[],
  action: ActionValue,
  resource?: Resource,
): boolean {
  if (!user) return false;
  if (user.is_admin) return true;
  if (AUTHENTICATED_ACTIONS.has(action)) return true;
  if (ADMIN_ONLY.has(action)) return false;
  if (TEAM_MEMBER_ACTIONS.has(action)) {
    const teamId = resourceTeamId(resource);
    if (!teamId) return false;
    return memberships.some(
      (m) => m.team.id === teamId && m.role === "manager",
    );
  }

  // --- Knowledge Bases + Documents ----------------------------------------
  if (KB_CREATE_ACTIONS.has(action)) {
    if (!isKbResource(resource ?? ({} as KbResource))) return false;
    const r = resource as KbResource;
    return memberships.some(
      (m) =>
        m.team.id === r.owner_team_id &&
        (m.role === "manager" || m.role === "creator"),
    );
  }
  if (KB_ADMIN_ACTIONS.has(action)) {
    if (!isKbResource(resource ?? ({} as KbResource))) return false;
    const r = resource as KbResource;
    if (
      memberships.some(
        (m) => m.team.id === r.owner_team_id && m.role === "manager",
      )
    )
      return true;
    return r.kb_permission === "admin";
  }
  if (DOCUMENT_WRITE_ACTIONS.has(action)) {
    if (!isKbResource(resource ?? ({} as KbResource))) return false;
    const r = resource as KbResource;
    if (
      memberships.some(
        (m) =>
          m.team.id === r.owner_team_id &&
          (m.role === "manager" || m.role === "creator"),
      )
    )
      return true;
    return r.kb_permission === "write" || r.kb_permission === "admin";
  }

  // --- M3 Fase 5: MCP servers ----------------------------------------------
  // Resource shape: { owner_team_id: string }
  if (MCP_CREATE_ACTIONS.has(action)) {
    if (!isKbResource(resource ?? ({} as KbResource))) return false;
    const r = resource as KbResource;
    return memberships.some(
      (m) =>
        m.team.id === r.owner_team_id &&
        (m.role === "manager" || m.role === "creator"),
    );
  }
  if (MCP_ADMIN_ACTIONS.has(action)) {
    if (!isKbResource(resource ?? ({} as KbResource))) return false;
    const r = resource as KbResource;
    return memberships.some(
      (m) => m.team.id === r.owner_team_id && m.role === "manager",
    );
  }

  // --- Skills (paridade com MCP) -------------------------------------------
  if (SKILL_CREATE_ACTIONS.has(action)) {
    if (!isKbResource(resource ?? ({} as KbResource))) return false;
    const r = resource as KbResource;
    return memberships.some(
      (m) =>
        m.team.id === r.owner_team_id &&
        (m.role === "manager" || m.role === "creator"),
    );
  }
  if (SKILL_ADMIN_ACTIONS.has(action)) {
    if (!isKbResource(resource ?? ({} as KbResource))) return false;
    const r = resource as KbResource;
    return memberships.some(
      (m) => m.team.id === r.owner_team_id && m.role === "manager",
    );
  }

  // --- M4 Fase 6: agentes ---------------------------------------------------
  // Create: manager OR creator do owner_team alvo.
  if (AGENT_CREATE_ACTIONS.has(action)) {
    if (!isKbResource(resource ?? ({} as KbResource))) return false;
    const r = resource as AgentResource;
    return memberships.some(
      (m) =>
        m.team.id === r.owner_team_id &&
        (m.role === "manager" || m.role === "creator"),
    );
  }
  // Edit (update/delete/duplicate/etc): admin OR criador OR manager
  // do owner_team. ``created_by_user_id`` deve estar no resource para
  // o branch funcionar fora de admin.
  if (AGENT_EDIT_ACTIONS.has(action)) {
    if (!isKbResource(resource ?? ({} as KbResource))) return false;
    const r = resource as AgentResource;
    if (
      memberships.some(
        (m) => m.team.id === r.owner_team_id && m.role === "manager",
      )
    )
      return true;
    return r.created_by_user_id === user.id;
  }
  // Rate: qualquer autenticado pode avaliar o que ve (visibilidade vem
  // do backend; aqui apenas habilita o botao).
  if (AGENT_AUTH_ACTIONS.has(action)) return true;

  return false;
}
